// src/contexts/app-update-context.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { APP_VERSION } from '../version';
import { AppLoader } from '../components/app-loader/app-loader';
import { UpdateRequiredScreen } from '../components/update-required-screen';
import { UpdateAvailableBanner } from '../components/update-available-banner';
import { isNative, getPlatform } from '../platform';
import { compareSemver } from '../helpers/semver';
import { API_BASE_URL } from '../env';

/* ── Types ── */

export type UpdateStatus =
  | 'checking'
  | 'up-to-date'
  | 'update-required'
  | 'update-available'
  | 'offline'
  | 'updating';

type AppUpdateContextValue = {
  status: UpdateStatus;
  currentVersion: string;
  latestVersion: string | null;
  triggerUpdate: () => void;
  dismissUpdate: () => void;
};

const AppUpdateContext = createContext<AppUpdateContextValue | null>(null);

/* ── Helpers ── */

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version ?? null;
  } catch {
    return null;
  }
}

/* ── Provider ── */

export const AppUpdateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [status, setStatus] = useState<UpdateStatus>('checking');
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const dismissedRef = useRef(false);
  const didBootCheckRef = useRef(false);
  const nativeStoreUrlRef = useRef<string | null>(null);

  /* ── Boot-time version check ── */
  useEffect(() => {
    if (didBootCheckRef.current) return;
    didBootCheckRef.current = true;

    if (isNative()) {
      let cancelled = false;

      (async () => {
        try {
          const { App } = await import('@capacitor/app');
          const info = await App.getInfo();
          const currentVersion = info.version; // e.g. "1.0.0"

          const res = await fetch(`${API_BASE_URL}/app-version/check`, {
            cache: 'no-store',
          });
          if (!res.ok) {
            if (!cancelled) setStatus('up-to-date'); // fail open
            return;
          }
          const data = await res.json();

          if (cancelled) return;

          setLatestVersion(data.latest_version ?? null);

          if (compareSemver(currentVersion, data.min_version) < 0) {
            nativeStoreUrlRef.current =
              getPlatform() === 'android'
                ? data.store_url_android
                : data.store_url_ios;
            setStatus('update-required');
          } else {
            setStatus('up-to-date');
          }
        } catch {
          if (!cancelled) setStatus('up-to-date'); // fail open on error
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    // ── Web/PWA version check ──
    let cancelled = false;

    (async () => {
      const remote = await fetchLatestVersion();

      if (cancelled) return;

      if (remote === null) {
        // Network error / offline — don't block the app
        setStatus('offline');
        return;
      }

      setLatestVersion(remote);

      if (remote === APP_VERSION) {
        setStatus('up-to-date');
      } else {
        setStatus('update-required');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Listen for SW "new content available" event ── */
  useEffect(() => {
    if (isNative()) return; // No SW updates on native
    const handler = () => {
      // Only show banner if app already passed boot check
      if (status === 'up-to-date' || status === 'offline') {
        dismissedRef.current = false;
        setStatus('update-available');
      }
    };

    window.addEventListener('pwa:update-available', handler);
    return () => window.removeEventListener('pwa:update-available', handler);
  }, [status]);

  /* ── Re-check version when app comes to foreground ── */
  useEffect(() => {
    const canRecheck = status === 'up-to-date' || status === 'offline' || status === 'update-available';

    const checkRemote = async () => {
      if (!canRecheck) return;

      if (isNative()) {
        try {
          const { App } = await import('@capacitor/app');
          const info = await App.getInfo();
          const res = await fetch(`${API_BASE_URL}/app-version/check`, { cache: 'no-store' });
          if (!res.ok) return;
          const data = await res.json();
          setLatestVersion(data.latest_version ?? null);
          if (compareSemver(info.version, data.min_version) < 0) {
            nativeStoreUrlRef.current =
              getPlatform() === 'android' ? data.store_url_android : data.store_url_ios;
            dismissedRef.current = false;
            setStatus('update-required');
          }
        } catch { /* fail silent */ }
        return;
      }

      const remote = await fetchLatestVersion();
      if (!remote) return;

      setLatestVersion(remote);

      if (remote !== APP_VERSION) {
        dismissedRef.current = false;
        setStatus('update-available');
      }
    };

    // visibilitychange — works on both Chrome and Safari standalone
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkRemote();
    };

    // pageshow — fires on iOS Safari when restoring from app switcher (bfcache)
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) checkRemote();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);

    // Native: also listen for Capacitor appStateChange (more reliable than visibilitychange)
    let nativeCleanup: (() => void) | undefined;
    if (isNative()) {
      import('@capacitor/app').then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) checkRemote();
        }).then((handle) => {
          nativeCleanup = () => handle.remove();
        });
      }).catch(() => {});
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      nativeCleanup?.();
    };
  }, [status]);

  /* ── Actions ── */

  const triggerUpdate = useCallback(async () => {
    if (isNative()) {
      const url = nativeStoreUrlRef.current;
      if (url) {
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url });
        } catch { /* fallback: do nothing */ }
      }
      return;
    }
    setStatus('updating');
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    // Clear all caches
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    // Hard reload
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => {
    dismissedRef.current = true;
    // Go back to showing children without the banner
    setStatus('up-to-date');
  }, []);

  const value = useMemo<AppUpdateContextValue>(
    () => ({
      status,
      currentVersion: APP_VERSION,
      latestVersion,
      triggerUpdate,
      dismissUpdate,
    }),
    [status, latestVersion, triggerUpdate, dismissUpdate],
  );

  /* ── Render based on status ── */

  // Boot-time: checking for updates
  if (status === 'checking') {
    return <AppLoader active label="Checking for updates" />;
  }

  // Boot-time: version mismatch — block everything
  if (status === 'update-required') {
    return (
      <AppUpdateContext.Provider value={value}>
        <UpdateRequiredScreen />
      </AppUpdateContext.Provider>
    );
  }

  // User triggered reload
  if (status === 'updating') {
    return <AppLoader active label="Updating" />;
  }

  // Normal operation (up-to-date, offline, or update-available)
  return (
    <AppUpdateContext.Provider value={value}>
      {children}
      {status === 'update-available' && !dismissedRef.current && (
        <UpdateAvailableBanner />
      )}
    </AppUpdateContext.Provider>
  );
};

export const useAppUpdate = () => {
  const ctx = useContext(AppUpdateContext);
  if (!ctx) throw new Error('useAppUpdate must be used within AppUpdateProvider');
  return ctx;
};
